import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ChevronRight } from "lucide-react";

const SCOPES = [
  { v: "no_pre", label: "Total (sin pretemp.)" },
  { v: "competition", label: "Competición" },
  { v: "preseason", label: "Pretemporada" },
  { v: "all", label: "Todo" },
];
// metric -> {label, group(att|match), accessor, countLabel, playerRoute}
const METRICS = {
  attendance: { label: "% Asistencia", group: "att",
    pct: (r) => r.att.pct, count: (r) => `${r.att.attended}/${r.att.computable}`,
    pending: (r) => r.att.pending, route: "detalle-asistencia" },
  called: { label: "% Convocatorias", group: "match",
    pct: (r) => r.match.called_pct, count: (r) => `${r.match.called}/${r.match.played}`,
    pending: (r) => r.match.pending, route: "detalle-partidos", qmetric: "called" },
  participation: { label: "% Participación", group: "match",
    pct: (r) => r.match.participation_pct, count: (r) => `${r.match.participated}/${r.match.played}`,
    pending: (r) => r.match.pending, route: "detalle-partidos", qmetric: "participation" },
  minutes: { label: "% Minutos", group: "match",
    pct: (r) => r.match.minutes_pct, count: (r) => `${r.match.minutes}'/${r.match.available}'`,
    pending: (r) => r.match.pending, route: "detalle-partidos", qmetric: "minutes" },
};

const pctTxt = (v) => (v == null ? "Sin datos" : `${v}%`);

export default function TeamStatDetail() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const backSeason = sp.get("season") || "";

  const [scope, setScope] = useState(sp.get("scope") || "no_pre");
  const [range, setRange] = useState({ from: "", to: "" });
  const [metric, setMetric] = useState(METRICS[sp.get("metric")] ? sp.get("metric") : "attendance");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { scope };
    if (range.from) params.date_from = range.from;
    if (range.to) params.date_to = range.to;
    api.get(`/teams/${teamId}/stat-breakdown`, { params })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [teamId, scope, range.from, range.to]);

  const M = METRICS[metric];
  const rows = useMemo(() => {
    if (!data) return [];
    return [...data.players].sort((a, b) => (M.pct(b) ?? -1) - (M.pct(a) ?? -1));
  }, [data, M]);

  const teamPct = data ? (M.group === "att" ? data.totals.att.pct
    : metric === "called" ? data.totals.match.called_pct
      : metric === "participation" ? data.totals.match.participation_pct
        : data.totals.match.minutes_pct) : null;

  const openPlayer = (pid) => {
    const q = `?scope=${scope}${backSeason ? `&season=${backSeason}` : ""}${M.qmetric ? `&metric=${M.qmetric}` : ""}`;
    navigate(`/players/${pid}/${M.route}${q}`);
  };
  const goBack = () => navigate(
    `/dashboard?team_id=${teamId}${backSeason ? `&season=${backSeason}` : ""}`);

  if (loading && !data) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-purple-600" /></div>;
  }
  if (!data) return <p className="text-sm text-slate-400 py-10 text-center">No se pudo cargar el equipo.</p>;

  const empty = rows.every((r) => M.pct(r) == null);

  return (
    <div className="space-y-5 pb-10" data-testid="team-stat-detail">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={goBack} data-testid="team-stat-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Volver
        </Button>
        <div>
          <h1 className="text-xl font-extrabold text-slate-900">Detalle del equipo · {M.label}</h1>
          <p className="text-xs text-slate-500">{data.team?.name} · {data.players.length} jugadores</p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3" data-testid="team-stat-filters">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Indicador</label>
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-48" data-testid="team-stat-metric"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(METRICS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Periodo</label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-52" data-testid="team-stat-scope"><SelectValue /></SelectTrigger>
            <SelectContent>{SCOPES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Desde</label>
          <Input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="w-40" data-testid="team-stat-from" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Hasta</label>
          <Input type="date" value={range.to} min={range.from || undefined} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="w-40" data-testid="team-stat-to" />
        </div>
        <div className="ml-auto text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{M.label} equipo</div>
          <div className="text-2xl font-extrabold text-purple-700" data-testid="team-stat-aggregate">{pctTxt(teamPct)}</div>
        </div>
      </div>

      {empty ? (
        <Card className="border-slate-200"><CardContent className="py-12 text-center text-slate-400" data-testid="team-stat-nodata">Sin datos</CardContent></Card>
      ) : (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-extrabold text-slate-800">Desglose por jugador</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm" data-testid="team-breakdown-table">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-2 w-12">#</th><th className="py-2 px-2">Jugador</th>
                    <th className="py-2 px-2">Posición</th>
                    <th className="py-2 px-2 text-center">{M.label}</th>
                    <th className="py-2 px-2 text-center">Registros</th>
                    <th className="py-2 px-2 text-center">Pendientes</th>
                    <th className="py-2 px-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.player_id} className="border-b last:border-0 cursor-pointer hover:bg-purple-50" onClick={() => openPlayer(r.player_id)} data-testid={`team-breakdown-row-${r.player_id}`}>
                      <td className="py-2 pr-2 text-slate-400">{r.dorsal ?? "—"}</td>
                      <td className="py-2 px-2 font-semibold text-slate-700">{r.name}</td>
                      <td className="py-2 px-2 text-slate-500">{r.position || "—"}</td>
                      <td className="py-2 px-2 text-center font-extrabold text-purple-700">{pctTxt(M.pct(r))}</td>
                      <td className="py-2 px-2 text-center text-slate-600">{M.count(r)}</td>
                      <td className="py-2 px-2 text-center">{M.pending(r) ? <span className="text-amber-600 font-bold">{M.pending(r)}</span> : "—"}</td>
                      <td className="py-2 px-2 text-right"><ChevronRight className="h-4 w-4 text-slate-300" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile */}
            <div className="md:hidden space-y-2">
              {rows.map((r) => (
                <div key={r.player_id} className="border border-slate-200 rounded-lg p-3 flex items-center justify-between cursor-pointer" onClick={() => openPlayer(r.player_id)} data-testid={`team-breakdown-card-${r.player_id}`}>
                  <div>
                    <div className="font-bold text-slate-700 text-sm">{r.dorsal ? `${r.dorsal} · ` : ""}{r.name}</div>
                    <div className="text-xs text-slate-500">{r.position || "—"} · {M.count(r)}{M.pending(r) ? ` · ${M.pending(r)} pend.` : ""}</div>
                  </div>
                  <div className="text-xl font-extrabold text-purple-700">{pctTxt(M.pct(r))}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 text-center mt-2">Pulsa un jugador para ver sus registros exactos.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
